import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, TrendingUp, Clock, CheckCircle2, Loader2, Plus, Ban, Printer } from "lucide-react";
import { useBillingItems, useCreateBillingItem, useUpdateBillingItem, formatNaira } from "@/hooks/use-supabase-queries";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { toast } from "sonner";
import { BillingInvoice } from "./BillingInvoice";
import { UpgradeNotice } from "@/components/subscription/UpgradeNotice";
import { useSubscription } from "@/hooks/useSubscription";

const statusConfig: Record<string, { label: string; className: string }> = {
  paid: { label: "Paid", className: "bg-primary/10 text-primary" },
  pending: { label: "Pending", className: "bg-accent text-accent-foreground" },
  overdue: { label: "Overdue", className: "bg-destructive/10 text-destructive" },
  waived: { label: "Waived", className: "bg-muted text-muted-foreground" },
};

const categories = [
  { value: "legal-fee", label: "Legal Fee" },
  { value: "stamp-duty", label: "Stamp Duty" },
  { value: "registration", label: "Registration" },
  { value: "search-fee", label: "Search Fee" },
  { value: "disbursement", label: "Disbursement" },
  { value: "other", label: "Other" },
];

interface MatterBillingProps {
  matterId: string;
}

export function MatterBilling({ matterId }: MatterBillingProps) {
  const { user } = useAuth();
  const { entitlements } = useSubscription();
  const { data: items, isLoading, refetch } = useBillingItems(matterId);
  const createBilling = useCreateBillingItem();
  const updateBilling = useUpdateBillingItem();

  const [addOpen, setAddOpen] = useState(false);
  const [payConfirm, setPayConfirm] = useState<string | null>(null);
  const [waiveConfirm, setWaiveConfirm] = useState<string | null>(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [initializingPaymentId, setInitializingPaymentId] = useState<string | null>(null);

  // Add form state
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [dueDate, setDueDate] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get("reference");

    if (!reference) return;

    toast.success("Payment completed", {
      description: "Your payment was received and the billing list is refreshing.",
    });
    refetch();
    params.delete("reference");
    const nextUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    window.history.replaceState({}, "", nextUrl);
  }, [refetch]);

  if (isLoading) {
    return (
      <Card><CardContent className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </CardContent></Card>
    );
  }

  if (!entitlements.billingAndInvoicing) {
    return (
      <UpgradeNotice
        title="Billing and invoicing unlock on Professional"
        description="Matter fee breakdowns, invoices, and Paystack payment collection are reserved for Professional and Enterprise workspaces."
      />
    );
  }

  const activeItems = items?.filter(i => i.status !== 'waived') || [];
  const totalBilled = activeItems.reduce((sum, i) => sum + i.amount, 0);
  const paidAmount = activeItems.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);
  const pendingAmount = activeItems.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.amount, 0);
  const paidPercent = totalBilled > 0 ? Math.round((paidAmount / totalBilled) * 100) : 0;

  const handleAdd = async () => {
    if (!desc.trim() || !amount) return;
    const amountKobo = Math.round(parseFloat(amount) * 100);
    if (isNaN(amountKobo) || amountKobo <= 0) {
      toast.error("Please enter a valid positive amount");
      return;
    }
    try {
      await createBilling.mutateAsync({
        matter_id: matterId,
        description: desc.trim(),
        amount: amountKobo,
        category: category || undefined,
        due_date: dueDate || undefined,
      });
      toast.success("Billing item added");
      setAddOpen(false);
      setDesc(""); setAmount(""); setCategory(""); setDueDate("");
    } catch {
      toast.error("Failed to add billing item");
    }
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await updateBilling.mutateAsync({ id, status: 'paid', paid_at: new Date().toISOString() });
      // Audit logged automatically by database trigger
      // Notify client
      const { data: matter } = await supabase.from('matters').select('client_id, matter_number').eq('id', matterId).single();
      if (matter?.client_id) {
        const item = items?.find(i => i.id === id);
        await supabase.functions.invoke('create-notification', {
          body: {
            user_id: matter.client_id,
            title: 'Payment Recorded',
            message: `Payment of ${formatNaira(item?.amount || 0)} for "${item?.description}" has been recorded.`,
            type: 'billing',
            link_to: `/client-transaction/${matterId}`,
          },
        });
      }
      toast.success("Marked as paid");
      setPayConfirm(null);
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleWaive = async (id: string) => {
    try {
      await updateBilling.mutateAsync({ id, status: 'waived' });
      // Audit logged automatically by database trigger
      toast.success("Item waived");
      setWaiveConfirm(null);
    } catch {
      toast.error("Failed to waive");
    }
  };

  const handleInitializePayment = async (item: NonNullable<typeof items>[number]) => {
    if (!user?.email) {
      toast.error("No email found for your account");
      return;
    }

    setInitializingPaymentId(item.id);

    try {
      const { data, error } = await supabase.functions.invoke("create-payment", {
        body: {
          billing_item_id: item.id,
          callback_url: window.location.href,
        },
      });

      if (error) throw error;
      if (!data?.authorization_url) throw new Error("Payment link was not returned.");

      window.location.assign(data.authorization_url);
    } catch (err: any) {
      toast.error("Could not start payment", {
        description: err.message,
      });
      setInitializingPaymentId(null);
    }
  };

  const payItem = items?.find(i => i.id === payConfirm);
  const waiveItem = items?.find(i => i.id === waiveConfirm);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10"><Receipt className="h-5 w-5 text-primary" /></div>
          <div><p className="text-xl font-bold text-foreground">{formatNaira(totalBilled)}</p><p className="text-sm text-muted-foreground">Total Billed</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10"><CheckCircle2 className="h-5 w-5 text-primary" /></div>
          <div><p className="text-xl font-bold text-foreground">{formatNaira(paidAmount)}</p><p className="text-sm text-muted-foreground">Paid</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-accent/10"><Clock className="h-5 w-5 text-accent" /></div>
          <div><p className="text-xl font-bold text-foreground">{formatNaira(pendingAmount)}</p><p className="text-sm text-muted-foreground">Pending</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-muted"><TrendingUp className="h-5 w-5 text-muted-foreground" /></div>
          <div><p className="text-xl font-bold text-foreground">{formatNaira(totalBilled - paidAmount)}</p><p className="text-sm text-muted-foreground">Outstanding</p></div>
        </div></CardContent></Card>
      </div>

      {/* Payment Progress */}
      {totalBilled > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Payment Progress</span>
              <span className="text-sm text-muted-foreground">{paidPercent}% collected</span>
            </div>
            <Progress value={paidPercent} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Billing Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Billing Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground">Manage fees and payments for this matter</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setInvoiceOpen(true)}>
              <Printer className="h-4 w-4 mr-1" />Invoice
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />Add Fee
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!items || items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No billing items yet. Click "Add Fee" to get started.</p>
          ) : (
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <Table className="min-w-[600px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="hidden sm:table-cell">Category</TableHead>
                    <TableHead className="hidden sm:table-cell">Due Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const config = statusConfig[item.status || 'pending'] || statusConfig.pending;
                    return (
                      <TableRow key={item.id} className={item.status === 'waived' ? 'opacity-50' : ''}>
                        <TableCell className="font-medium">{item.description}</TableCell>
                        <TableCell className="hidden text-muted-foreground capitalize sm:table-cell">{item.category?.replace('-', ' ') || '-'}</TableCell>
                        <TableCell className="hidden text-muted-foreground sm:table-cell">{item.due_date ? format(new Date(item.due_date), 'MMM d, yyyy') : '-'}</TableCell>
                        <TableCell className="font-semibold">{formatNaira(item.amount)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col items-start gap-1">
                            <Badge className={config.className}>{config.label}</Badge>
                            {item.status === 'paid' && item.paid_at && (
                              <span className="text-xs text-muted-foreground">
                                Paid {format(new Date(item.paid_at), 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.status !== 'paid' && item.status !== 'waived' && (
                            <div className="flex flex-col items-end gap-1 sm:flex-row sm:justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleInitializePayment(item)}
                                disabled={initializingPaymentId === item.id}
                              >
                                {initializingPaymentId === item.id ? (
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : null}
                                Pay Now
                              </Button>
                            </div>
                          )}
                          {item.status === 'pending' && (
                            <div className="mt-1 flex flex-col items-end gap-1 sm:flex-row sm:justify-end">
                              <Button variant="ghost" size="sm" className="text-primary" onClick={() => setPayConfirm(item.id)}>
                                <CheckCircle2 className="h-4 w-4 mr-1" />Paid
                              </Button>
                              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setWaiveConfirm(item.id)}>
                                <Ban className="h-4 w-4 mr-1" />Waive
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Fee Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Billing Item</DialogTitle>
            <DialogDescription>Add a new fee or charge to this matter.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Description *</Label>
              <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="e.g. Legal consultation fee" />
            </div>
            <div>
              <Label>Amount (₦) *</Label>
              <Input type="number" min="1" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 350000" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={!desc.trim() || !amount || createBilling.isPending}>
              {createBilling.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Confirmation */}
      <Dialog open={!!payConfirm} onOpenChange={() => setPayConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
            <DialogDescription>Mark this billing item as paid?</DialogDescription>
          </DialogHeader>
          {payItem && (
            <div className="py-4 space-y-2">
              <p className="font-medium">{payItem.description}</p>
              <p className="text-2xl font-bold text-primary">{formatNaira(payItem.amount)}</p>
              <p className="text-sm text-muted-foreground">This will record the payment and notify the client.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayConfirm(null)}>Cancel</Button>
            <Button onClick={() => payConfirm && handleMarkPaid(payConfirm)} disabled={updateBilling.isPending}>
              {updateBilling.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Waive Confirmation */}
      <Dialog open={!!waiveConfirm} onOpenChange={() => setWaiveConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Waive Billing Item</DialogTitle>
            <DialogDescription>Are you sure you want to waive this fee?</DialogDescription>
          </DialogHeader>
          {waiveItem && (
            <div className="py-4 space-y-2">
              <p className="font-medium">{waiveItem.description}</p>
              <p className="text-xl font-bold text-foreground">{formatNaira(waiveItem.amount)}</p>
              <p className="text-sm text-muted-foreground">This item will be marked as waived and excluded from totals.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaiveConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => waiveConfirm && handleWaive(waiveConfirm)} disabled={updateBilling.isPending}>
              Waive Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice Preview</DialogTitle>
            <DialogDescription>Print or save this invoice for your records.</DialogDescription>
          </DialogHeader>
          <BillingInvoice matterId={matterId} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
